import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/SaveOutlined'
import LogoutIcon from '@mui/icons-material/LogoutOutlined'
import type { LoginResponse, StoreMembership } from '@iobiz/shared'
import { api } from '../api/client.js'
import { useSessionStore } from '../stores/sessionStore.js'
import { ChoixPortrait } from '../ui/ChoixPortrait.js'

/**
 * Son propre compte.
 *
 * ⚠️ À ne pas confondre avec la console plateforme, qui gère les comptes DES
 * AUTRES. Ici on ne trouve ni statut d'administrateur ni activation : on ne
 * s'accorde pas des droits à soi-même depuis son formulaire de profil — c'est
 * l'élévation de privilège classique.
 */
export function MonComptePage(): JSX.Element {
  const navigate = useNavigate()
  const { slug } = useParams<{ slug?: string }>()
  const { user, stores, logout, appliquerProfil } = useSessionStore()

  const [prenom, setPrenom] = useState('')
  const [nom, setNom] = useState('')
  const [email, setEmail] = useState('')
  const [tel, setTel] = useState('')
  const [portrait, setPortrait] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; texte: string } | null>(null)
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    if (!user) return
    setPrenom(user.firstName ?? '')
    setNom(user.lastName ?? '')
    setEmail(user.email)
    setTel(user.phone ?? '')
    setPortrait(user.avatarUrl ?? null)
  }, [user])

  const nomAffiche = `${prenom} ${nom}`.trim() || email

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h1" sx={{ mb: 1 }}>
        Mon compte
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
        Vos informations et votre mot de passe.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 4 }} onClose={() => setMessage(null)}>
          {message.texte}
        </Alert>
      )}

      <Paper sx={{ p: 6, borderRadius: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* La photo se change ICI, directement : c'est celle qui apparaît
              dans l'entête une fois connecté. */}
          <ChoixPortrait value={portrait} onChange={setPortrait} />
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h1">{nomAffiche}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {user?.email}
              {user?.isPlatformAdmin && ' · Administrateur de la plateforme'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={() => void logout()}
          >
            Se déconnecter
          </Button>
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gap: 4,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          alignItems: 'start',
        }}
      >
        <Paper sx={{ p: 6, borderRadius: 3 }}>
          <Typography variant="h3" sx={{ mb: 4 }}>
            Informations
          </Typography>
          <Box sx={{ display: 'grid', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Prénom"
                value={prenom}
                fullWidth
                onChange={(e) => setPrenom(e.target.value)}
              />
              <TextField
                label="Nom"
                value={nom}
                fullWidth
                onChange={(e) => setNom(e.target.value)}
              />
            </Box>
            <TextField
              label="Adresse e-mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              helperText="C’est l’identifiant de connexion : le changer change votre façon d’entrer."
            />
            <TextField label="Téléphone" value={tel} onChange={(e) => setTel(e.target.value)} />
            <Box>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={envoi}
                onClick={async () => {
                  setEnvoi(true)
                  setMessage(null)
                  try {
                    const compte = await api.patch<
                      LoginResponse['user'] & { stores: StoreMembership[] }
                    >('/auth/me', {
                      firstName: prenom.trim(),
                      lastName: nom.trim(),
                      email: email.trim(),
                      phone: tel.trim(),
                      avatarUrl: portrait,
                    })
                    appliquerProfil(compte, compte.stores)
                    setMessage({ type: 'success', texte: 'Informations enregistrées.' })
                  } catch (e) {
                    setMessage({
                      type: 'error',
                      texte: e instanceof Error ? e.message : 'Enregistrement impossible.',
                    })
                  } finally {
                    setEnvoi(false)
                  }
                }}
              >
                Enregistrer
              </Button>
            </Box>
          </Box>
        </Paper>

        <ChangementMotDePasse onMessage={setMessage} />
      </Box>

      <Paper sx={{ p: 6, borderRadius: 3, mt: 4 }}>
        <Typography variant="h3" sx={{ mb: 4 }}>
          Mes boutiques — {stores.length}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Boutique</TableCell>
              <TableCell>Rôle</TableCell>
              <TableCell>Devise</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stores.map((s) => (
              <TableRow key={s.storeId} hover>
                <TableCell>
                  <Link
                    component="button"
                    variant="body2"
                    underline="hover"
                    onClick={() => navigate(`/s/${s.slug}/dashboard`)}
                  >
                    {s.name}
                  </Link>
                  {slug === s.slug && (
                    <Chip size="small" label="Courante" sx={{ ml: 2 }} color="primary" />
                  )}
                </TableCell>
                <TableCell>{s.role}</TableCell>
                <TableCell>{s.currencyCode}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  )
}

function ChangementMotDePasse({
  onMessage,
}: {
  onMessage: (m: { type: 'success' | 'error'; texte: string }) => void
}): JSX.Element {
  const [actuel, setActuel] = useState('')
  const [nouveau, setNouveau] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const trop_court = nouveau.length > 0 && nouveau.length < 12
  const discordant = confirmation.length > 0 && confirmation !== nouveau

  return (
    <Paper sx={{ p: 6, borderRadius: 3 }}>
      <Typography variant="h3" sx={{ mb: 4 }}>
        Mot de passe
      </Typography>
      <Box sx={{ display: 'grid', gap: 3 }}>
        <TextField
          label="Mot de passe actuel"
          type="password"
          value={actuel}
          autoComplete="current-password"
          onChange={(e) => setActuel(e.target.value)}
        />
        <TextField
          label="Nouveau mot de passe"
          type="password"
          value={nouveau}
          autoComplete="new-password"
          error={trop_court}
          helperText={
            trop_court
              ? 'Au moins 12 caractères'
              : '12 caractères au minimum, avec majuscule, minuscule et chiffre.'
          }
          onChange={(e) => setNouveau(e.target.value)}
        />
        <TextField
          label="Confirmation"
          type="password"
          value={confirmation}
          autoComplete="new-password"
          error={discordant}
          helperText={discordant ? 'Ne correspond pas' : ' '}
          onChange={(e) => setConfirmation(e.target.value)}
        />
        <Alert severity="info">
          Vos autres sessions seront fermées. Celle-ci reste ouverte : vous n’avez pas à vous
          reconnecter ici.
        </Alert>
        <Box>
          <Button
            variant="contained"
            disabled={envoi || !actuel || nouveau.length < 12 || nouveau !== confirmation}
            onClick={async () => {
              setEnvoi(true)
              try {
                await api.post('/auth/password', {
                  currentPassword: actuel,
                  newPassword: nouveau,
                  confirmation,
                })
                setActuel('')
                setNouveau('')
                setConfirmation('')
                onMessage({ type: 'success', texte: 'Mot de passe changé.' })
              } catch (e) {
                onMessage({
                  type: 'error',
                  texte: e instanceof Error ? e.message : 'Changement impossible.',
                })
              } finally {
                setEnvoi(false)
              }
            }}
          >
            Changer le mot de passe
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}
